import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useUserProfile, getInitials } from '../hooks/useUserProfile';
import ManageAccountModal from './ManageAccountModal';
import './Header.css';

function Header({ onCompose, onMenuToggle, searchTerm, onSearchChange, user, onSignOut }) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [manageAccountOpen, setManageAccountOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef(null);
  const { userProfile, loading } = useUserProfile(user);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [userMenuOpen]);

  return (
    <header className="main-header">
      <div className="header-left">
        <button className="icon-button" onClick={onMenuToggle} title="Menu">
          <i className="fas fa-bars"></i>
        </button>
        <div className="logo">
          <i className="fas fa-envelope"></i>
          <span>ITCPR</span>
        </div>
      </div>
      
      <div className="header-center">
        <div className="search-bar">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search mail"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      
      <div className="header-right">
        <button className="btn-primary" onClick={onCompose}>
          <i className="fas fa-pen"></i>
          Compose
        </button>
        
        <div className="user-menu" ref={menuRef}>
          <button
            className="user-menu-button"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className="user-avatar">
              {userProfile?.photoURL ? (
                <img src={userProfile.photoURL} alt={userProfile.name} />
              ) : (
                <span>{getInitials(userProfile?.name || 'User')}</span>
              )}
            </div>
            <i className="fas fa-chevron-down"></i>
          </button>
          
          {userMenuOpen && (() => {
            const dropdownContent = (
              <div className={`user-dropdown ${userMenuOpen ? 'show' : ''}`}>
                <div className="dropdown-header">
                  <div className="user-info">
                    <div className="user-avatar">
                      {userProfile?.photoURL ? (
                        <img src={userProfile.photoURL} alt={userProfile.name} />
                      ) : (
                        <span>{getInitials(userProfile?.name || 'User')}</span>
                      )}
                    </div>
                    <div className="user-details">
                      <div className="user-name">{userProfile?.name || 'User'}</div>
                      <div className="user-email">{user?.email || ''}</div>
                    </div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <div className="dropdown-items">
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setManageAccountOpen(true);
                    }}
                  >
                    <i className="fas fa-user-circle"></i>
                    <span>Manage Account</span>
                  </button>
                  <div className="dropdown-divider"></div>
                  <button className="dropdown-item" onClick={onSignOut}>
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            );

            // Use portal on mobile with overlay like ManageAccountModal
            if (isMobile) {
              return createPortal(
                <div 
                  className="user-dropdown-overlay" 
                  onClick={() => setUserMenuOpen(false)}
                >
                  <div 
                    className="user-dropdown-wrapper"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {dropdownContent}
                  </div>
                </div>,
                document.body
              );
            }

            return dropdownContent;
          })()}
        </div>
      </div>

      <ManageAccountModal
        isOpen={manageAccountOpen}
        onClose={() => setManageAccountOpen(false)}
        user={user}
      />
    </header>
  );
}

export default Header;
